import { isLifetimePeriod } from '../utils/subscription-format';
/**
 * App-store links + IDs for review/rating deep-links.
 */
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.empathease.app';

/** Set 2026-07-22 with the first public App Store release (v1.8). Setting this
 *  also activates the landing page's apple-itunes-app smart banner. */
export const IOS_APP_STORE_ID = '6738966090';

/** Public App Store listing (the download CTA target). */
export const IOS_APP_STORE_URL = `https://apps.apple.com/app/id${IOS_APP_STORE_ID}`;

/**
 * Store URL that opens the review/rating surface for the given platform, or
 * null when no usable URL exists (e.g. iOS pre-launch) — in which case the
 * caller should fall back to the in-app review API.
 */
export function storeReviewUrl(platform: string): string | null {
  if (platform === 'android') {
    return PLAY_STORE_URL;
  }
  if (platform === 'ios' && IOS_APP_STORE_ID) {
    return `https://apps.apple.com/app/id${IOS_APP_STORE_ID}?action=write-review`;
  }
  return null;
}

/** Universal Apple subscription-management URL (needs no app id). */
export const APPLE_SUBSCRIPTIONS_URL = 'https://apps.apple.com/account/subscriptions';

/** Universal Google Play subscription-management URL. */
export const PLAY_SUBSCRIPTIONS_URL = 'https://play.google.com/store/account/subscriptions';

/**
 * True when the STORE (Apple/Google) — not us — bills this subscription.
 *
 * Two things we must NEVER do for these:
 *  - cancel locally: the store keeps charging, so the user "cancels" and is still
 *    billed (and Apple rejects apps that fake-manage store subscriptions);
 *  - send them to web checkout: they'd end up double-billed with a parallel
 *    Stripe subscription running alongside the store one.
 * Both paths must instead deep-link to the store's own subscription settings.
 */
export function isStoreManaged(platform?: string | null): boolean {
  return platform === 'ios' || platform === 'android';
}

/** Where a store-managed subscriber manages / changes / cancels their plan. */
export function storeSubscriptionsUrl(platform?: string | null): string | null {
  if (platform === 'ios') {
    return APPLE_SUBSCRIPTIONS_URL;
  }
  if (platform === 'android') {
    return PLAY_SUBSCRIPTIONS_URL;
  }
  return null;
}

/** Human name of the store that bills a platform-managed subscription. */
export function storeName(platform?: string | null): string | null {
  if (platform === 'ios') {
    return 'the App Store';
  }
  if (platform === 'android') {
    return 'Google Play';
  }
  return null;
}

/** The billing company behind a platform-managed subscription. */
export function billingCompany(platform?: string | null): string | null {
  if (platform === 'ios') {
    return 'Apple';
  }
  if (platform === 'android') {
    return 'Google';
  }
  return null;
}

/** Just enough of a Subscription for the manage decision — keeps this file dependency-free. */
export interface ManageableSubscription {
  status?: string;
  platform?: string | null;
  /** Null means it never ends. Absent means the caller did not say. */
  expires_at?: string | null;
  billing_period?: string | null;
  /** Whether the processor will charge again — part of the lifetime decision. */
  auto_renewing?: boolean | null;
}

/**
 * What the user can actually DO about their subscription, given who bills it.
 *
 * One source of truth, because getting this wrong costs real money in both
 * directions: offering web checkout to someone Apple already bills double-charges
 * them, and offering an in-app cancel to them "cancels" nothing while the store
 * keeps charging. The profile card and the manage modal both key off this, so
 * they cannot drift apart (they had: the card and the modal disagreed about what
 * a cancelled App Store subscriber should see).
 *
 * Note `store` is returned for a cancelled store sub too. Their subscription is
 * still live until it lapses, and the ONLY place to re-enable it is the store —
 * sending them to web checkout instead would leave them paying us on Stripe AND
 * paying Apple the moment they re-enable there.
 */
export type ManageAction =
  | { kind: 'none' }
  | { kind: 'trial' }
  | { kind: 'lifetime' }
  | { kind: 'comped' }
  | { kind: 'stripe'; cancelled: boolean }
  | { kind: 'store'; platform: 'ios' | 'android'; url: string; store: string; company: string };

export function resolveManageAction(subscription?: ManageableSubscription | null): ManageAction {
  if (!subscription) {
    return { kind: 'none' };
  }

  const platform = subscription.platform;

  // Provenance first: a comped or debug grant is not a PURCHASE, and calling
  // it "a one-time purchase" tells a user they bought something they were
  // given. The lifetime test below is platform-agnostic by design, so without
  // this an admin grant (platform 'admin', expires_at null) fell straight into
  // it (Codex #830).
  if (platform === 'admin' || platform === 'debug') {
    return { kind: 'comped' };
  }

  // Before any platform question, because a one-time purchase has nothing
  // recurring to manage no matter who sold it. Keyed only on platform, this
  // offered a Stripe lifetime buyer "Cancel Subscription" for a subscription
  // that does not renew, and sent an App Store lifetime buyer to the store's
  // SUBSCRIPTIONS page, where their purchase does not appear at all
  // (Codex #830).
  //
  // Two signals, because neither alone is sound. The plan's billing_period is
  // the direct statement, but it is null whenever the product_id maps to no
  // plan row. An explicitly null expires_at is the other, and it must be
  // EXPLICIT: a caller that simply omits the key has not told us the
  // subscription never ends, and treating absence as "lifetime" made every
  // partial fixture look like a one-time purchase.
  // A renewing agreement is never a lifetime purchase, whatever its dates say.
  // The server draws this line too (Subscription::applyEntitledNow): undated
  // AND not renewing is a permanent grant; undated AND renewing is a row that
  // lost its expiry and may still be charging. Keying on the date alone hid the
  // cancel and store controls for exactly those rows (Codex #830).
  const neverExpires =
    !subscription.auto_renewing &&
    (isLifetimePeriod(subscription.billing_period) ||
      ('expires_at' in subscription && subscription.expires_at == null));

  if (neverExpires) {
    return { kind: 'lifetime' };
  }

  if (platform === 'trial') {
    return { kind: 'trial' };
  }

  if (isStoreManaged(platform)) {
    const url = storeSubscriptionsUrl(platform);
    const store = storeName(platform);
    const company = billingCompany(platform);
    // Guarded by isStoreManaged, but don't hand a caller a half-built action.
    if (url && store && company) {
      return { kind: 'store', platform: platform as 'ios' | 'android', url, store, company };
    }
    return { kind: 'none' };
  }

  if (platform === 'stripe') {
    return { kind: 'stripe', cancelled: subscription.status === 'cancelled' };
  }

  return { kind: 'none' };
}
